import { Injectable } from '@angular/core';
import { from, type Observable } from 'rxjs';
import {
  type AdminTourListDto,
  deleteAdminTourAsync,
  createAdminTourAsync,
  type AdminTourPostPutDto,
  type AdminTourGetDto,
  updateAdminTourAsync,
  listAdminToursAsync,
  findAdminTourAsync,
  getStopQrCodeAsync,
  listQrCodesAsync,
  type ListQrCodesAsyncData,
  type GetStopQrCodeAsyncData,
} from '../../../../backend/main';
import type { DtoListService, ListPaginatedDTO, GetAllRequestParameters } from '@pct/ng-core-dto';
import { UNKNOWN_ITEM } from '@pct/ng-core-dto';
import { isNil } from '@pct/ts-typeguard';
import type { FilteredParams } from '../../../common/request/filtered-params';
import type { TourFilter } from './tour-filter';
import { convertPaginationParams } from '../../../common/utils/table/convert-pagination-params';
import { mapSort } from '../../../common/request/map-sort';
import type { Options } from '../../../../backend/file';
import { getFilenameFromHeader } from '../../../common/utils/get-filename-from-header';
import type { FileUploadResponse } from '../../../common/file-upload/native/service/file-upload-response';

type ListDto = AdminTourListDto;

@Injectable()
export class TourDtoService implements DtoListService<
  ListDto,
  FilteredParams<ListDto, TourFilter>
> {
  getAll(
    param: GetAllRequestParameters<FilteredParams<ListDto, TourFilter>>,
  ): Observable<ListPaginatedDTO<ListDto>> {
    return from(
      listAdminToursAsync({
        body: {
          pagination: convertPaginationParams(param.pagination),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          sort: mapSort(param.parameters?.sort) as any,
          searchText: param.parameters?.filter?.searchText,
        },
      }).then(resp => resp.data),
    );
  }

  delete(id: string): Observable<void> {
    return from(deleteAdminTourAsync({ path: { id } }).then(resp => resp.data));
  }

  getDtoId(dto: ListDto): string;
  getDtoId(dto: ListDto | null | undefined): string | null | undefined;
  getDtoId(dto: Partial<ListDto> | null | undefined): string | null | undefined;
  getDtoId(dto: ListDto | Partial<ListDto> | null | undefined): string | null | undefined {
    if (isNil(dto)) {
      return dto;
    }
    return dto.id;
  }

  toString(dto: ListDto): string {
    return dto.title ?? UNKNOWN_ITEM;
  }

  getById(id: string): Observable<AdminTourGetDto> {
    return from(findAdminTourAsync({ path: { id } }).then(resp => resp.data));
  }

  create(data: AdminTourPostPutDto): Observable<void> {
    return from(
      createAdminTourAsync({ body: data }).then(() => {
        //VOID
      }),
    );
  }

  update(id: string, data: AdminTourPostPutDto): Observable<void> {
    return from(updateAdminTourAsync({ path: { id }, body: data }).then(resp => resp.data));
  }

  getQrCode(stopId: string): Observable<FileUploadResponse<string>> {
    return from(
      getStopQrCodeAsync({ path: { stopId }, responseType: 'blob' } as Options<
        GetStopQrCodeAsyncData,
        true
      >).then(({ data, response }) => ({
        file: new File([data], getFilenameFromHeader(response.headers), {
          type: response.headers?.get('content-type') ?? 'application/octet-stream',
        }),
      })),
    );
  }

  listQrCodes(id: string): Observable<FileUploadResponse<string>> {
    return from(
      listQrCodesAsync({ path: { id }, responseType: 'blob' } as Options<
        ListQrCodesAsyncData,
        true
      >).then(({ data, response }) => ({
        file: new File([data], getFilenameFromHeader(response.headers), {
          type: response.headers?.get('content-type') ?? 'zip',
        }),
      })),
    );
  }
}
